import { memo, useEffect } from "react";
import { Navigate, Route, Routes as BrowserRoutes, useLocation } from "react-router-dom";

// --- Auth domain ---
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import { useIsAdmin } from "@auth/hooks/useIsAdmin";
import LoginPage from "@auth/pages/LoginPage";
import RedirectPage from "@auth/pages/RedirectPage";
import OnboardingPage from "@auth/pages/OnboardingPage";
import ForgotPasswordPage from "@auth/pages/ForgotPasswordPage";
import InviteRegistrationPage from "@auth/pages/InviteRegistrationPage";
import RegistrationPage from "@auth/pages/RegistrationPage";
import ResetPasswordPage from "@auth/pages/ResetPasswordPage";
import VerifyEmailPage from "@auth/pages/VerifyEmailPage";

// --- Dashboard domain ---
import DashboardPage from "@dashboard/pages/DashboardPage";
import AboutPage from "@dashboard/pages/AboutPage";
import UserGuidePage from "@dashboard/pages/UserGuidePage";
import ContactPage from "@dashboard/pages/ContactPage";

// --- Chat domain ---
import ChatPage from "@chat/pages/ChatPage";
import ChatListPage from "@chat/pages/ChatListPage";

// --- Documents domain ---
import LexiconPage from "@documents/pages/LexiconPage";

// --- Extras domain ---
import CompassPage from "@extras/pages/CompassPage";
import VideoPage from "@extras/pages/VideoPage";

// --- Shop domain ---
import SaasShopPage from "@shop/pages/SaasShopPage";
import AIServiceShopPage from "@shop/pages/AIServiceShopPage";
import ConsultingShopPage from "@shop/pages/ConsultingShopPage";
import CheckoutStripeReturnPage from "@shop/pages/CheckoutStripeReturnPage";
import OnboardingShopPage from "@shop/pages/OnboardingShopPage";
import ExtraPagesShopPage from "@shop/pages/ExtraPagesShopPage";
import CheckoutRedirectHandler from "@shop/components/CheckoutRedirectHandler";

// --- Services domain ---
import AIServicesPage from "@services/pages/AIServicesPage";
import WesentlichkeitJobResultPage from "@services/pages/WesentlichkeitJobResultPage";
import WesentlichkeitJobStatusPage from "@services/pages/WesentlichkeitJobStatusPage";
import WesentlichkeitPage from "@services/pages/WesentlichkeitPage";
import WesentlichkeitServicePage from "@services/pages/WesentlichkeitServicePage";
import VsmeServicePage from "@services/pages/VsmeServicePage";
import VsmeJobResultPage from "@services/pages/VsmeJobResultPage";
import VsmeJobStatusPage from "@services/pages/VsmeJobStatusPage";

// --- Settings domain ---
import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import SettingsLayout from "@settings/base/SettingsLayout";
import { ChatSettingsPage } from "@settings/pages/app/ChatSettingsPage";
import { SettingsDashboardPage } from "@settings/pages/app/SettingsDashboardPage";
import { TestSettingsPage } from "@settings/pages/app/TestSettingsPage";
import SubscriptionOverviewPage from "@settings/pages/subscription/SubscriptionOverviewPage";
import LawSelectionPage from "@settings/pages/laws/LawSelectionPage";
import ConsolidatedCompanyProfilePage from "@settings/pages/company/ConsolidatedCompanyProfilePage";
import ReadOnlyCompanyProfilePage from "@settings/pages/company/ReadOnlyCompanyProfilePage";
import { UserManagementPage } from "@settings/pages/company/UserManagementPage";
import ProfileSettingsPage from "@settings/pages/profile/ProfileSettingsPage";
import { ProjectDetailsSettingsPage } from "@settings/pages/profile/ProjectDetailsSettingsPage";
import { ProjectSettingsPage } from "@settings/pages/profile/ProjectSettingsPage";

// --- Types/Enums ---
import { RouteTo, RouteToSettings } from "@routing/RouteMap";
import { useSubscriptionAccess } from "@company/hooks/useSubscriptionAccess";

// --- Static Pages ---
import PrivacyPolicyPage from "@static/pages/PrivacyPolicyPage";
import TermsAndConditionsPage from "@static/pages/TermsAndConditionsPage";

// Navigation tracking helper
const isSettingsPage = (path: string): boolean => {
    return path.startsWith("/settings");
};

// Navigation observer component
const NavigationTracker = () => {
    const location = useLocation();

    useEffect(() => {
        const currentPath = location.pathname;

        // If we're not on a settings page, store this as the last non-settings page
        if (!isSettingsPage(currentPath)) {
            localStorage.setItem("lastNonSettingsPath", currentPath);
            console.log("Stored last non-settings path:", currentPath);
        }
    }, [location.pathname]);

    return null;
};

// AdminRoute component - redirects non-admins to dashboard
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAdmin, loading } = useIsAdmin();

    // Don't redirect while loading - wait for the data to be available
    if (loading) {
        return null; // or a loading spinner if preferred
    }

    if (!isAdmin) {
        return <Navigate to={RouteTo.DASHBOARD} />;
    }

    return <>{children}</>;
};

/**
 * Redirects authenticated users away from routes meant only for guests (login, signup).
 */
const GuestRoute = ({ children }: { children: React.ReactNode }) => {
    const isAuthenticated = useIsAuthenticated();

    if (isAuthenticated) {
        return <Navigate to={RouteTo.DASHBOARD} />;
    }

    return <>{children}</>;
};

/**
 * Verhindert, dass eingeloggte User die Onboarding-Seite sehen.
 */
const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
    // Allow if not authenticated or if user still doesn't have subscription access
    const isAuthenticated = useIsAuthenticated();
    const { hasAccess, loading } = useSubscriptionAccess();

    if (loading) {
        // While we don't know yet, just render nothing (or a spinner if you have one)
        return null;
    }

    // Redirect users who are already fully onboarded (have active access)
    if (isAuthenticated && hasAccess) {
        return <Navigate to={RouteTo.DASHBOARD} />;
    }

    return <>{children}</>;
};

/**
 * Wird im {@link Layout} eingebunden, um dynamisch die Seite zu rendern, auf dessen Route man sich gerade befindet.
 * Es gibt Routen, die nur aufrufbar sind, wenn {@link useIsAuthenticated} `true` ist.
 *
 * Wenn die App über https://app.esgbot.de aufgerufen wird, prüft {@link RedirectPage}, ob zu `/login` oder `/dashboard` gewechselt wird,
 * abhängig von {@link useIsAuthenticated}.
 */
const Routes = memo(() => {
    const isAuthenticated = useIsAuthenticated();
    const { isAdmin } = useIsAdmin();

    return (
        <>
            {/* Track navigation for settings back button */}
            <NavigationTracker />
            
            {/* Automatische Weiterleitung zu unterbrochenen Checkout-Prozessen */}
            {isAuthenticated && <CheckoutRedirectHandler />}

            <BrowserRoutes>
                <Route path={RouteTo.LOGIN} element={<GuestRoute><LoginPage /></GuestRoute>} />
                {/* Aktuell nicht erreichbar, stattdessen /onboarding nutzen */}
                {/* <Route path={RouteTo.SIGNUP} element={<GuestRoute><RegistrationPage /></GuestRoute>} /> */}
                <Route path={RouteTo.ONBOARDING} element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />

                {/* Email Verification */}
                <Route path="/verify-email" element={<VerifyEmailPage />} />

                {/* Password Reset */}
                <Route path={RouteTo.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                <Route path={RouteTo.RESET_PASSWORD} element={<ResetPasswordPage />} />

                {/* Invite Registration */}
                <Route path={`${RouteTo.INVITE}/:code`} element={<InviteRegistrationPage />} />

                {isAuthenticated && (
                    <>
                        <Route path={RouteTo.SHOP} element={<OnboardingShopPage />} />

                        <Route path={RouteTo.DASHBOARD} element={<DashboardPage />} />
                        <Route path={RouteTo.CHAT} element={<ChatListPage />} />
                        <Route path={`${RouteTo.CHAT}/:id`} element={<ChatPage />} />
                        <Route path={RouteTo.WESENTLICHKEIT} element={<WesentlichkeitPage />} />
                        <Route path={`${RouteTo.WESENTLICHKEIT}/:einstellungen`} element={<WesentlichkeitPage />} />
                        <Route path={`${RouteTo.WESENTLICHKEIT}/:analyse`} element={<WesentlichkeitPage />} />
                        <Route path={`${RouteTo.WESENTLICHKEIT}/:analyse/:doc`} element={<WesentlichkeitPage />} />
                        <Route path={`${RouteTo.WESENTLICHKEIT}/:iro`} element={<WesentlichkeitPage />} />
                        <Route path={RouteTo.COMPASS} element={<CompassPage />} />
                        <Route path={RouteTo.LEXIKON} element={<LexiconPage />} />
                        <Route path={RouteTo.VIDEOS} element={<VideoPage />} />
                        <Route path={RouteTo.ABOUT} element={<AboutPage />} />
                        <Route path={RouteTo.AI_SERVICES} element={<AIServicesPage />} />

                        {/* Quick-Wesentlichkeit Service */}
                        <Route path={`${RouteTo.AI_SERVICES}/wesentlichkeit`} element={<WesentlichkeitServicePage />} />
                        <Route path={`${RouteTo.AI_SERVICES}/wesentlichkeit/status/:id`} element={<WesentlichkeitJobStatusPage />} />
                        <Route path={`${RouteTo.AI_SERVICES}/wesentlichkeit/result/:id`} element={<WesentlichkeitJobResultPage />} />

                        {/* VSME Report Generation Service */}
                        <Route path={`${RouteTo.AI_SERVICES}/vsme`} element={<VsmeServicePage />} />
                        <Route path={`${RouteTo.AI_SERVICES}/vsme/status/:id`} element={<VsmeJobStatusPage />} />
                        <Route path={`${RouteTo.AI_SERVICES}/vsme/result/:id`} element={<VsmeJobResultPage />} />

                        {/* Settings routes */}
                        <Route path={RouteToSettings.ROOT} element={<SettingsLayout />}>
                            {/* Default settings route */}
                            <Route index element={<Navigate to={RouteToSettings.DEFAULT} replace />} />

                            {/* App Settings */}
                            <Route path={RouteToSettings.DASHBOARD} element={<SettingsDashboardPage />} />
                            <Route path={RouteToSettings.CHAT} element={<ChatSettingsPage />} />

                            {/* Profile Settings */}
                            <Route path={RouteToSettings.PROFILE_GENERAL} element={<ProfileSettingsPage />} />
                            <Route path={RouteToSettings.PROFILE_PROJECTS} element={<ProjectSettingsPage />} />
                            <Route path={RouteToSettings.PROFILE_PROJECTS + "/:id"} element={<ProjectDetailsSettingsPage />} />
                            {/* <Route path={RouteToSettings.PROFILE_NEWSLETTER} element={<NewsletterSettingsPage />} /> */}

                            {/* Company Settings - Admin Only */}
                            <Route
                                path={RouteToSettings.COMPANY_PROFILE}
                                element={
                                    <AdminRoute>
                                        <ConsolidatedCompanyProfilePage />
                                    </AdminRoute>
                                }
                            />
                            <Route
                                path={RouteToSettings.COMPANY_PROFILE_READONLY}
                                element={isAdmin ? <Navigate to={RouteToSettings.COMPANY_PROFILE} /> : <ReadOnlyCompanyProfilePage />}
                            />
                            <Route path={RouteToSettings.COMPANY_PROJECTS} element={<BaseSettingsPage title="Firmenprojekte" />} />
                            <Route path={RouteToSettings.COMPANY_PROJECTS + "/:id"} element={<BaseSettingsPage title="Projekt Details" />} />
                            <Route
                                path={RouteToSettings.COMPANY_USERS}
                                element={
                                    <AdminRoute>
                                        <UserManagementPage />
                                    </AdminRoute>
                                }
                            />
                            <Route
                                path={RouteToSettings.COMPANY_SUBSCRIPTION}
                                element={
                                    <AdminRoute>
                                        <SubscriptionOverviewPage />
                                    </AdminRoute>
                                }
                            />
                            <Route path={RouteToSettings.LAW_SELECTION} element={<LawSelectionPage />} />
                            <Route
                                path={RouteToSettings.COMPANY_USERS + "/:id"}
                                element={
                                    <AdminRoute>
                                        <UserManagementPage />
                                    </AdminRoute>
                                }
                            />

                            <Route path={RouteToSettings.SHOP_SAAS} element={<SaasShopPage />} />
                            <Route path={RouteToSettings.SHOP_AI_SERVICE} element={<AIServiceShopPage />} />
                            <Route path={RouteToSettings.SHOP_CONSULTING} element={<ConsultingShopPage />} />
                            <Route path={RouteToSettings.SHOP_EXTRA_PAGES} element={<ExtraPagesShopPage />} />

                            <Route path={RouteToSettings.TEST} element={<TestSettingsPage />} />
                        </Route>

                        <Route path={"/payment/success/:sessionId"} element={<CheckoutStripeReturnPage />} />
                        <Route path={"/payment/cancel/:sessionId"} element={<CheckoutStripeReturnPage />} />
                    </>
                )}

                <Route path={RouteTo.USER_GUIDE} element={<UserGuidePage />} />

                {/* Contact form (public) */}
                <Route path={RouteTo.CONTACT} element={<ContactPage />} />

                {/* Other routes */}
                <Route path={RouteTo.PRIVACY} element={<PrivacyPolicyPage />} />
                <Route path={RouteTo.AGB} element={<TermsAndConditionsPage />} />
                <Route path={RouteTo.LEGAL} element={<TermsAndConditionsPage />} />

                <Route path={"*"} element={<RedirectPage />} />
            </BrowserRoutes>
        </>
    );
});

export default Routes;
